import apiUrls from "src/api";
import type { SpeechLanguage } from "../types/audio";
import { stripMarkdown } from "../utils/stripMarkdown";

interface TokenResponse {
  token: string;
  voiceId: string;
  modelId: string;
  languageCode: string;
}

interface SessionOptions {
  language: SpeechLanguage;
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (error: Error) => void;
}

async function fetchToken(language: SpeechLanguage): Promise<TokenResponse> {
  const jwt = localStorage.getItem("wixar.auth.jwt");
  const url = `${apiUrls.voiceMode.elevenlabsToken}?language=${language}`;
  const response = await fetch(url, {
    method: "GET",
    headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
  });
  if (!response.ok) {
    throw new Error(`ElevenLabs token fetch failed: ${response.status}`);
  }
  return (await response.json()) as TokenResponse;
}

function base64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function findLastSentenceBoundary(text: string): number {
  const re = /[.!?…]\s/g;
  let last = -1;
  let m;
  while ((m = re.exec(text)) !== null) {
    last = m.index + m[0].length;
  }
  return last;
}

class ElevenLabsStreamingService {
  private ws: WebSocket | null = null;
  private mediaSource: MediaSource | null = null;
  private sourceBuffer: SourceBuffer | null = null;
  private audio: HTMLAudioElement | null = null;
  private audioObjectUrl: string | null = null;
  private pendingAudio: Uint8Array<ArrayBuffer>[] = [];
  private chunkBuffer = "";
  private sessionOptions: SessionOptions | null = null;
  private playbackStarted = false;
  private streamEnded = false;
  private cancelled = false;
  private endRequested = false;

  /**
   * Open a streaming session: fetch token, open WS to ElevenLabs, set up audio
   * sink. Resolves when the WS is open and ready to receive text via pushText.
   * Audio playback fires asynchronously via the onStart/onEnd callbacks.
   */
  async beginSession(options: SessionOptions): Promise<void> {
    this.stop();
    this.cancelled = false;
    this.sessionOptions = options;
    this.chunkBuffer = "";
    this.endRequested = false;

    let tokenInfo: TokenResponse;
    try {
      tokenInfo = await fetchToken(options.language);
      console.log("[voice-mode] elevenlabs session opening", {
        voiceId: tokenInfo.voiceId,
        modelId: tokenInfo.modelId,
        languageCode: tokenInfo.languageCode,
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      options.onError?.(error);
      throw error;
    }

    if (this.cancelled) return;

    return new Promise<void>((resolve, reject) => {
      const onWsOpen = () => {
        try {
          const ws = this.ws;
          if (!ws) {
            reject(new Error("WebSocket not initialized"));
            return;
          }
          ws.send(
            JSON.stringify({
              text: " ",
              voice_settings: { stability: 0.5, similarity_boost: 0.75 },
            }),
          );
          this.flushBufferedSentences();
          if (this.endRequested) {
            this.endRequested = false;
            this.finalizeSessionFlush();
          }
          resolve();
        } catch (err) {
          reject(err instanceof Error ? err : new Error(String(err)));
        }
      };

      const onWsError = (err: Error) => {
        options.onError?.(err);
        reject(err);
      };

      try {
        this.setupAudio();
      } catch (err) {
        return onWsError(err instanceof Error ? err : new Error(String(err)));
      }

      const params = new URLSearchParams({
        model_id: tokenInfo.modelId,
        single_use_token: tokenInfo.token,
        language_code: tokenInfo.languageCode,
        output_format: "mp3_44100_128",
        auto_mode: "true",
      });
      const wsUrl = `wss://api.elevenlabs.io/v1/text-to-speech/${
        tokenInfo.voiceId
      }/stream-input?${params.toString()}`;

      const ws = new WebSocket(wsUrl);
      this.ws = ws;

      console.log(
        "[voice-mode] opening WS",
        wsUrl.replace(/single_use_token=[^&]+/, "single_use_token=***"),
      );

      ws.onopen = () => {
        console.log("[voice-mode] WS open");
        if (this.cancelled) {
          ws.close();
          return;
        }
        onWsOpen();
      };

      ws.onmessage = (event) => {
        if (this.cancelled) return;
        let msg: { audio?: string; isFinal?: boolean; error?: string; message?: string };
        try {
          msg = JSON.parse(event.data as string) as {
            audio?: string;
            isFinal?: boolean;
            error?: string;
            message?: string;
          };
        } catch {
          console.warn("[voice-mode] non-JSON WS message:", event.data);
          return;
        }
        if (msg.error || msg.message) {
          console.error("[voice-mode] server error frame:", msg);
        }
        if (msg.error) {
          this.failPlayback(new Error(`ElevenLabs error: ${msg.error}`));
          return;
        }
        if (msg.audio) {
          if (!this.playbackStarted) {
            console.log("[voice-mode] first audio chunk received");
          }
          const bytes = base64ToBytes(msg.audio);
          this.appendAudio(bytes);
          if (!this.playbackStarted) {
            this.playbackStarted = true;
            this.sessionOptions?.onStart?.();
            this.audio?.play().catch((err) => {
              console.error("[voice-mode] audio play() failed:", err);
            });
          }
        }
        if (msg.isFinal) {
          console.log("[voice-mode] isFinal received");
          this.streamEnded = true;
          this.endMediaStreamIfReady();
        }
      };

      ws.onerror = (event) => {
        console.error("[voice-mode] WS error event:", event);
        onWsError(new Error("ElevenLabs WebSocket error"));
        this.failPlayback(new Error("ElevenLabs WebSocket error"));
      };

      ws.onclose = (event) => {
        console.log("[voice-mode] WS close", {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
        });
        this.streamEnded = true;
        this.endMediaStreamIfReady();
      };
    });
  }

  /**
   * Push a chunk of text. Always buffers; only flushes complete
   * sentences when the WS is open, so chunks arriving during the
   * WS handshake aren't dropped on the floor.
   */
  pushText(chunk: string): void {
    if (!chunk) return;
    this.chunkBuffer += chunk;
    this.flushBufferedSentences();
  }

  private flushBufferedSentences(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const boundary = findLastSentenceBoundary(this.chunkBuffer);
    if (boundary <= 0) return;
    const ready = this.chunkBuffer.slice(0, boundary);
    this.chunkBuffer = this.chunkBuffer.slice(boundary);
    const cleaned = stripMarkdown(ready).trim();
    if (cleaned) {
      console.log("[voice-mode] → text", { len: cleaned.length, preview: cleaned.slice(0, 60) });
      this.ws.send(JSON.stringify({ text: cleaned + " " }));
    }
  }

  /**
   * Flush any remaining buffered text and signal end-of-input to the WS.
   * If the WS is still connecting (handshake in flight), the request is
   * deferred and replayed in onopen so that short bot replies aren't dropped.
   */
  endSession(): void {
    if (!this.ws) return;
    if (this.ws.readyState !== WebSocket.OPEN) {
      this.endRequested = true;
      return;
    }
    this.finalizeSessionFlush();
  }

  private finalizeSessionFlush(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const remainder = this.chunkBuffer.trim();
    this.chunkBuffer = "";
    if (remainder) {
      const cleaned = stripMarkdown(remainder).trim();
      if (cleaned) {
        console.log("[voice-mode] → tail", { len: cleaned.length, preview: cleaned.slice(0, 60) });
        this.ws.send(JSON.stringify({ text: cleaned + " " }));
      }
    }
    console.log("[voice-mode] → EOS");
    this.ws.send(JSON.stringify({ text: "" }));
  }

  /**
   * One-shot wrapper: open a session, push the entire text, signal EOS, and
   * wait for playback to fully complete. Used for code paths that have the
   * complete bot reply before TTS starts (e.g. legacy speak-on-stream-end).
   */
  async speak(text: string, options: SessionOptions): Promise<void> {
    console.log("[voice-mode] elevenLabsStreamingService.speak", {
      lang: options.language,
      length: text.length,
    });
    if (!text.trim()) {
      options.onEnd?.();
      return;
    }
    return new Promise<void>((resolve, reject) => {
      this.beginSession({
        ...options,
        onEnd: () => {
          options.onEnd?.();
          resolve();
        },
        onError: (err) => {
          options.onError?.(err);
          reject(err);
        },
      })
        .then(() => {
          this.pushText(text);
          this.endSession();
        })
        .catch((err) => {
          reject(err instanceof Error ? err : new Error(String(err)));
        });
    });
  }

  stop(): void {
    this.cancelled = true;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    if (this.audio) {
      this.audio.pause();
    }
    this.cleanup();
  }

  private setupAudio(): void {
    if (typeof MediaSource === "undefined") {
      throw new Error("MediaSource Extensions not supported in this browser");
    }
    if (!MediaSource.isTypeSupported("audio/mpeg")) {
      throw new Error("audio/mpeg not supported by MediaSource");
    }

    this.mediaSource = new MediaSource();
    this.audioObjectUrl = URL.createObjectURL(this.mediaSource);
    this.audio = new Audio(this.audioObjectUrl);
    this.audio.preload = "auto";

    this.audio.onended = () => {
      const opts = this.sessionOptions;
      this.cleanup();
      opts?.onEnd?.();
    };
    this.audio.onerror = () => {
      this.failPlayback(new Error("Audio element playback failed"));
    };

    this.mediaSource.addEventListener(
      "sourceopen",
      () => {
        try {
          const mediaSource = this.mediaSource;
          if (!mediaSource) {
            this.failPlayback(new Error("MediaSource not initialized"));
            return;
          }
          this.sourceBuffer = mediaSource.addSourceBuffer("audio/mpeg");
          this.sourceBuffer.addEventListener("updateend", () => {
            this.flushPending();
            this.endMediaStreamIfReady();
          });
          this.flushPending();
        } catch (err) {
          this.failPlayback(err instanceof Error ? err : new Error(String(err)));
        }
      },
      { once: true },
    );
  }

  private failPlayback(error: Error): void {
    const opts = this.sessionOptions;
    this.cleanup();
    opts?.onError?.(error);
  }

  private appendAudio(bytes: Uint8Array<ArrayBuffer>): void {
    if (!this.sourceBuffer || this.sourceBuffer.updating) {
      this.pendingAudio.push(bytes);
      return;
    }
    try {
      this.sourceBuffer.appendBuffer(bytes);
    } catch (err) {
      console.error("appendBuffer failed:", err);
      this.pendingAudio.push(bytes);
    }
  }

  private flushPending(): void {
    if (!this.sourceBuffer || this.sourceBuffer.updating) return;
    const next = this.pendingAudio.shift();
    if (!next) return;
    try {
      this.sourceBuffer.appendBuffer(next);
    } catch (err) {
      console.error("appendBuffer (flushPending) failed:", err);
    }
  }

  private endMediaStreamIfReady(): void {
    if (!this.streamEnded) return;
    if (this.pendingAudio.length > 0) return;
    if (!this.mediaSource) return;
    if (this.mediaSource.readyState !== "open") return;
    if (this.sourceBuffer?.updating) return;
    this.mediaSource.endOfStream();
  }

  private cleanup(): void {
    if (this.audioObjectUrl) {
      URL.revokeObjectURL(this.audioObjectUrl);
      this.audioObjectUrl = null;
    }
    this.audio = null;
    this.sourceBuffer = null;
    this.mediaSource = null;
    this.pendingAudio = [];
    this.chunkBuffer = "";
    this.sessionOptions = null;
    this.playbackStarted = false;
    this.streamEnded = false;
    this.endRequested = false;
  }
}

export const elevenLabsStreamingService = new ElevenLabsStreamingService();
